import { useEffect, useMemo, useState } from 'react'
import { Share, PlusSquare, Smartphone, Download, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Card } from '@/components/ui/card'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>
}

export default function InstallShortcutButton() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIosHelp, setShowIosHelp] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true

    if (standalone) {
      setIsInstalled(true)
    } else {
      // Small delay to make it feel smooth
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
      setShowIosHelp(false)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''

  const isIos = useMemo(
    () => /iphone|ipad|ipod/i.test(ua),
    [ua]
  )

  const isSafari = useMemo(
    () => /^((?!chrome|android|crios|fxios).)*safari/i.test(ua),
    [ua]
  )

  async function handleInstallClick() {
    if (deferredPrompt) {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      if (outcome === 'accepted') {
        setDeferredPrompt(null)
      }
      return
    }

    if (isIos) {
      setShowIosHelp(true)
    }
  }

  // Only show on mobile or if deferredPrompt is available
  const shouldShow = isVisible && !isInstalled && (deferredPrompt || isIos)

  if (!shouldShow) return null

  return (
    <>
      <Card className="p-4 border-none bg-white shadow-sm ring-1 ring-black/5 animate-in fade-in slide-in-from-top-4 duration-500">
        <div className="flex items-start gap-4">
          <div className="bg-primary/10 p-3 rounded-2xl">
            <Download className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 space-y-1">
            <h3 className="font-semibold text-slate-900 leading-tight">
              {isIos ? "Guarda este acceso en tu iPhone" : "Instala este acceso"}
            </h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              {isIos 
                ? "Añádelo a tu pantalla de inicio para usarlo como una app."
                : "Guárdalo en tu pantalla inicial para entrar más rápido todos los días."}
            </p>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button 
            onClick={handleInstallClick}
            className="flex-1 rounded-xl h-12 text-base font-semibold shadow-md active:scale-95 transition-all"
          >
            {isIos ? "Cómo añadir a pantalla inicial" : "Instalar app"}
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setIsVisible(false)}
            className="rounded-xl h-12 w-12 text-slate-400"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </Card>

      <Drawer open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DrawerContent className="px-6 pb-10">
          <DrawerHeader className="px-0 pb-6">
            <DrawerTitle className="text-xl font-bold text-slate-900">
              Instrucciones para iPhone
            </DrawerTitle>
            <DrawerDescription className="text-slate-500">
              Sigue estos pasos para añadir la app a tu pantalla de inicio.
            </DrawerDescription>
          </DrawerHeader>
          
          <div className="space-y-6">
            {!isSafari && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 flex gap-3">
                <Smartphone className="w-5 h-5 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800 font-medium">
                  En iPhone, esta opción funciona mejor desde Safari.
                </p>
              </div>
            )}
            
            <div className="grid gap-6">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 font-bold text-sm">1</div>
                <p className="text-slate-700 font-medium">Abre este sitio en Safari.</p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 font-bold text-sm">2</div>
                <div className="flex items-center gap-2 text-slate-700 font-medium">
                  Toca el botón Compartir <div className="p-1.5 bg-blue-50 rounded-lg"><Share className="w-4 h-4 text-blue-600" /></div>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 font-bold text-sm">3</div>
                <div className="flex items-center gap-2 text-slate-700 font-medium">
                  Selecciona “Añadir a pantalla de inicio” <div className="p-1.5 bg-slate-50 rounded-lg"><PlusSquare className="w-4 h-4 text-slate-600" /></div>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-900 font-bold text-sm">4</div>
                <p className="text-slate-700 font-medium">Toca “Añadir”.</p>
              </div>
            </div>
          </div>
          
          <DrawerFooter className="px-0 pt-8">
            <DrawerClose asChild>
              <Button variant="outline" className="w-full rounded-xl h-12 text-base">Entendido</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}
